import { CheckIcon, ChevronRightIcon, CloseIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { cn } from "cn";
import { useState } from "react";

import type {
  AgentPart,
  ConfirmationDecision,
  ConfirmationItem,
  ConfirmationResult,
} from "../agent-event.ts";
import {
  actionResult,
  summarizeResults,
  type CardOutcome,
} from "../decisions.ts";

type ConfirmationPart = Extract<AgentPart, { type: "confirmation_request" }>;
type ActionResult = ConfirmationResult["results"][number];

/**
 * Tarjeta de confirmación (sección 8.2).
 *
 * Toda acción de escritura del agente pasa por aquí. Es lo que permite que el
 * agente proponga sin que nadie tenga que confiar en que acierte: enseña qué
 * va a hacer y con qué datos exactos, antes de hacerlo.
 *
 * Y, una vez decidida, lo que pasó DE VERDAD (R2.1): aprobar no es hacer. En
 * un grupo de ocho, que una falle es justo lo que hay que ver, así que la
 * cabecera lo resume y cada línea dice lo suyo.
 */
export function Confirmation({
  part,
  outcome: given,
  onResolve,
  disabled,
}: {
  part: ConfirmationPart;
  /** Cómo está, según el backend y lo decidido en esta sesión. */
  outcome: CardOutcome;
  onResolve: (actionId: string, decision: ConfirmationDecision) => void;
  disabled: boolean;
}) {
  const [decided, setDecided] = useState<ConfirmationDecision | null>(null);

  /**
   * Una tarjeta resuelta se dibuja sin botones: ofrecer el botón sería una
   * trampa -- el segundo clic chocaría contra el agregado y el usuario no
   * sabría por qué.
   *
   * El `decided` de aquí solo cubre el instante entre el clic y el siguiente
   * render; lo que manda es lo que llega de arriba.
   */
  const outcome: CardOutcome =
    given.kind === "waiting" && decided !== null
      ? { kind: "deciding", decision: decided }
      : given;
  const waiting = outcome.kind === "waiting";

  const decide = (decision: ConfirmationDecision) => {
    // Se bloquea al instante: sin esto, un doble clic manda dos peticiones.
    // La clave de idempotencia del backend evitaría la doble ejecución, pero
    // el usuario no debería ver dos respuestas por un clic de más.
    if (decided !== null) return;
    setDecided(decision);
    onResolve(part.actionId, decision);
  };

  const items = part.items ?? null;
  const many = items !== null ? items.length : 1;
  const failed =
    outcome.kind === "approved" || outcome.kind === "approved_and_remembered"
      ? summarizeResults(outcome.results).failed
      : 0;
  const single =
    items === null ? actionResult(outcome, part.actionId) : undefined;

  return (
    /* El borde de la izquierda es lo que distingue una tarjeta que pide algo
       de una que solo informa, sin tener que leerla. Lo que falló no se
       apaga: es lo que hay que leer. */
    <Card
      className={cn(
        "gap-3 border-l-4 py-4",
        waiting && "border-l-warning",
        failed > 0 && "border-l-destructive",
        !waiting && failed === 0 && "opacity-65",
      )}
    >
      <CardHeader className="gap-2 px-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge outcome={outcome} />
          {items === null ? (
            <code className="text-muted-foreground text-xs">
              {part.toolName}
            </code>
          ) : (
            <span className="text-muted-foreground text-xs">
              {many === 1 ? "1 acción" : `${String(many)} acciones`}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 px-4">
        <p className="whitespace-pre-line">{part.summary}</p>

        {items === null ? (
          <InputList input={part.input} />
        ) : (
          <GroupList items={items} outcome={outcome} />
        )}

        {single?.status === "failed" && (
          <p className="text-destructive text-sm">
            Falló al ejecutarse{single.error !== null && `: ${single.error}`}
          </p>
        )}
      </CardContent>

      <CardFooter className="px-4">
        {waiting ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={disabled}
              onClick={() => {
                decide("approve");
              }}
            >
              {many === 1 ? "Aprobar" : `Aprobar las ${String(many)}`}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              title={`Aprobar y no volver a preguntar por ${part.toolName} en esta conversación`}
              onClick={() => {
                decide("approve_and_remember");
              }}
            >
              Aprobar y recordar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => {
                decide("reject");
              }}
            >
              {many === 1 ? "Rechazar" : "Rechazar todas"}
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            {closingLine(outcome, many)}
          </p>
        )}
      </CardFooter>
    </Card>
  );
}

/** La cabecera: qué pide mientras espera, y qué pasó cuando ya no. */
function StatusBadge({ outcome }: { outcome: CardOutcome }) {
  const [text, tone] = ((): [string, string] => {
    switch (outcome.kind) {
      case "waiting":
        return ["Requiere tu confirmación", "border-warning text-warning"];
      case "approved":
      case "approved_and_remembered": {
        const { text, failed } = summarizeResults(outcome.results);
        const remembered =
          outcome.kind === "approved_and_remembered" ? " · recordada" : "";
        return [
          `${text}${remembered}`,
          failed > 0
            ? "border-destructive text-destructive"
            : "border-success text-success",
        ];
      }
      case "deciding":
        return [
          outcome.decision === "reject"
            ? "Rechazada"
            : outcome.decision === "approve_and_remember"
              ? "Aprobada · recordada"
              : "Aprobada",
          "",
        ];
      case "rejected":
        return ["Rechazada", ""];
      case "expired":
        return ["Caducó", ""];
      case "resolved":
        return ["Resuelta", ""];
    }
  })();

  return (
    <Badge variant="outline" className={tone}>
      {text}
    </Badge>
  );
}

/** La frase del pie, cuando ya no hay nada que decidir. */
const closingLine = (outcome: CardOutcome, many: number): string => {
  const one = many === 1;
  switch (outcome.kind) {
    case "waiting":
      return "";
    case "deciding":
      if (outcome.decision === "reject") {
        return one
          ? "Rechazaste esta acción."
          : `Rechazaste las ${String(many)} acciones.`;
      }
      return one
        ? "Aprobaste esta acción."
        : `Aprobaste las ${String(many)} acciones.`;
    case "approved":
    case "approved_and_remembered": {
      const { failed } = summarizeResults(outcome.results);
      const remembered =
        outcome.kind === "approved_and_remembered"
          ? ` No volverá a preguntar por ${one ? "esta herramienta" : "estas herramientas"} en esta conversación.`
          : "";
      if (failed === 0) {
        return (one ? "Se hizo." : "Se hicieron todas.") + remembered;
      }
      return (
        (one
          ? "Se aprobó, pero no se pudo hacer."
          : "Las demás se hicieron igual: una que falla no deshace el resto. Despliega la que falló para ver por qué.") +
        remembered
      );
    }
    case "rejected":
      return one
        ? "Rechazada: no se hizo nada."
        : "Rechazadas: no se hizo ninguna.";
    case "expired":
      return "Caducó sin respuesta: no se hizo nada.";
    case "resolved":
      return one
        ? "Esta acción ya se resolvió o caducó."
        : "Estas acciones ya se resolvieron o caducaron.";
  }
};

/** Lo que el sistema pone y el usuario no eligió: no se enseña. */
const HIDDEN_FIELDS = new Set(["userId", "conversationId"]);

const fieldsOf = (input: unknown): [string, unknown][] =>
  Object.entries((input as Record<string, unknown> | null) ?? {}).filter(
    ([key]) => !HIDDEN_FIELDS.has(key),
  );

const showValue = (value: unknown): string =>
  typeof value === "object" && value !== null
    ? JSON.stringify(value)
    : String(value);

/** Los datos exactos con los que se va a ejecutar una acción. */
function InputList({ input }: { input: unknown }) {
  const entries = fieldsOf(input);
  if (entries.length === 0) return null;

  return (
    <dl className="grid gap-px overflow-hidden rounded-md border text-sm">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="bg-secondary/40 flex flex-wrap gap-x-3 px-3 py-1.5"
        >
          <dt className="text-muted-foreground">{key}</dt>
          <dd className="ml-auto text-right break-all">{showValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Las acciones de un grupo, una por línea (1.6).
 *
 * La línea enseña los DATOS de cada acción y no su resumen: en un grupo el
 * resumen empieza igual en todas --"Da de alta un cargo…"-- porque la
 * descripción es la de la herramienta, y lo que distingue una acción de la de
 * al lado queda al final, donde no se lee. Qué hacen ya lo dice el resumen
 * del grupo, justo encima. El resumen entero de cada una, y a qué herramienta
 * va, siguen a un clic: agrupar la decisión no puede costar perder de vista
 * con qué se ejecuta cada acción.
 */
function GroupList({
  items,
  outcome,
}: {
  items: readonly ConfirmationItem[];
  outcome: CardOutcome;
}) {
  return (
    <ol className="scroll-quiet grid max-h-72 gap-px overflow-auto rounded-md border text-sm">
      {items.map((item, index) => {
        const result = actionResult(outcome, item.actionId);
        return (
          <li key={item.actionId} className="bg-secondary/40">
            <details className="group" open={result?.status === "failed"}>
              <summary className="flex cursor-pointer list-none items-baseline gap-2 px-3 py-1.5">
                <span className="text-muted-foreground w-5 shrink-0 text-right text-xs tabular-nums">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  <ItemLine item={item} />
                </span>
                {result !== undefined && <ItemStatus result={result} />}
                <ChevronRightIcon className="text-muted-foreground size-3.5 shrink-0 self-center transition-transform group-open:rotate-90" />
              </summary>
              <div className="space-y-1 px-3 pb-2 pl-10">
                {result?.status === "failed" && (
                  <p className="text-destructive">
                    Falló{result.error !== null && `: ${result.error}`}
                  </p>
                )}
                <p className="whitespace-pre-line">{item.summary}</p>
                <code className="text-muted-foreground text-xs">
                  {item.toolName}
                </code>
              </div>
            </details>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Una acción en una línea: sus datos, `clave valor · clave valor`.
 *
 * Sin datos que enseñar, la primera línea de su resumen, que es lo único que
 * queda para reconocerla.
 */
function ItemLine({ item }: { item: ConfirmationItem }) {
  const fields = fieldsOf(item.input);
  if (fields.length === 0) return <>{item.summary.split("\n")[0]}</>;

  return (
    <>
      {fields.map(([key, value], index) => (
        <span key={key}>
          {index > 0 && <span className="text-muted-foreground"> · </span>}
          <span className="text-muted-foreground">{key} </span>
          {showValue(value)}
        </span>
      ))}
    </>
  );
}

/** Lo que pasó con una acción del grupo, en una palabra y con su marca. */
function ItemStatus({ result }: { result: ActionResult }) {
  return result.status === "done" ? (
    <span className="text-success flex shrink-0 items-center gap-1 text-xs">
      <CheckIcon className="size-3.5" aria-hidden />
      hecha
    </span>
  ) : (
    <span className="text-destructive flex shrink-0 items-center gap-1 text-xs">
      <CloseIcon className="size-3.5" aria-hidden />
      falló
    </span>
  );
}
